import { AlertCircle, Loader2, X } from 'lucide-react';

import { cn } from '@/lib/utils';

import { documentLabel } from './documentLabels';
import { RelatedDocument } from './types';

interface Props {
    item: RelatedDocument;
    onRemove: () => void;
}

function ChipStatus({ item }: { item: RelatedDocument }) {
    if (item.status === 'copying') {
        return (
            <span className="flex items-center gap-1 font-sans font-normal text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                Copying…
            </span>
        );
    }
    if (item.status === 'error') {
        return (
            <span className="flex items-center gap-1 font-sans font-normal">
                <AlertCircle className="h-3 w-3 shrink-0" aria-hidden="true" />
                {item.errorMessage}
            </span>
        );
    }
    return null;
}

/** A picked document: its reference, the asset, a copy status and a remove button. */
export function RelatedDocumentChip({ item, onRemove }: Props) {
    const { document, status } = item;
    const label = documentLabel(document);
    return (
        <span
            className={cn(
                'inline-flex min-h-[30px] max-w-full items-start gap-1 rounded-md pl-3 pr-1 font-mono text-[13px] font-medium',
                status === 'error'
                    ? 'bg-destructive/10 text-destructive'
                    : 'bg-primary/10 text-primary',
            )}
        >
            <span className="flex min-w-0 flex-wrap items-center gap-x-2 py-1">
                <span>
                    <span className="whitespace-nowrap">{label}</span>
                    {document.asset && (
                        <span className="font-normal opacity-80">
                            {' · '}
                            {document.asset}
                        </span>
                    )}
                </span>
                <ChipStatus item={item} />
            </span>
            <button
                type="button"
                aria-label={`Remove ${label}`}
                onClick={onRemove}
                className="mt-1 flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded hover:bg-black/10 focus-visible:ring-2 focus-visible:ring-ring"
            >
                <X className="h-3 w-3" aria-hidden="true" />
            </button>
        </span>
    );
}
